/*
  Embed anywhere: helpers for when Club Wealth University runs inside a member's own website.

  Inside a frame on another website, the browser gives CWU a separate storage area for that website, so
  'CWAuthToken' there is the embed's own session (minted by /api/public/embed/exchange), never the
  member's normal login. These helpers only switch on when CWU is framed AND an embed key was saved by
  the /embed page, so the normal website is untouched.
*/

const EMBED_KEY = 'cwuEmbedKey';
const PARENT_ORIGIN = 'cwuEmbedParent';

export const isFramed = (): boolean => {
  try {
    return window.self !== window.top;
  } catch (e) {
    return true; // a cross-origin parent throws: we are framed
  }
};

const read = (k: string): string | null => {
  try {
    return window.localStorage.getItem(k);
  } catch (e) {
    return null;
  }
};

const write = (k: string, v: string) => {
  try {
    window.localStorage.setItem(k, v);
  } catch (e) {
    // storage blocked: the embed falls back to the sign-in button every visit
  }
};

export const embedKey = (): string | null => (isFramed() ? read(EMBED_KEY) : null);
export const inEmbed = (): boolean => !!embedKey();

/** The website this frame sits in, as the browser reports it ("https://portal.team.com"), or null. */
export function parentOrigin(): string | null {
  try {
    const ancestors = (window.location as any).ancestorOrigins;
    if (ancestors && ancestors.length) return ancestors[ancestors.length - 1] || null;
  } catch (e) {
    // not supported (Firefox): use the referrer below
  }
  const saved = read(PARENT_ORIGIN);
  if (saved) return saved;
  try {
    return document.referrer ? new URL(document.referrer).origin : null;
  } catch (e) {
    return null;
  }
}

export function rememberEmbed(key: string, origin: string | null) {
  write(EMBED_KEY, key);
  if (origin) write(PARENT_ORIGIN, origin);
}

/** Where a signed-out person goes: the embed's sign-in card inside a frame, the login page elsewhere. */
export function loginPath(): string {
  const key = embedKey();
  return key ? `/embed?key=${encodeURIComponent(key)}` : '/login';
}

/** Where the embed opens once signed in, from what the member chose to show. */
export function startPath(view: { mode: string; courseId?: number } | null | undefined): string {
  if (view && view.mode === 'course' && view.courseId) return `/courses/viewCourse/${view.courseId}`;
  if (view && view.mode === 'coaching') return '/boards/coaching';
  return '/';
}

let resizeStarted = false;
/**
 * Tells the website around the frame how tall CWU is, so the frame grows to fit (embed.js listens).
 * Only ever posts to the website the frame sits in.
 */
export function startEmbedResize() {
  if (resizeStarted || !inEmbed()) return;
  const target = parentOrigin();
  if (!target) return;
  resizeStarted = true;
  let last = 0;
  let lastGrowth = 0;
  let sameGrowth = 0;
  const send = () => {
    const h = Math.ceil(document.documentElement.scrollHeight);
    if (Math.abs(h - last) < 4) return;
    // A page sized to the window (100vh plus a header) grows by the same step every time the frame
    // grows. Stop following it after a few identical steps instead of growing forever.
    const growth = h - last;
    sameGrowth = growth > 0 && growth === lastGrowth ? sameGrowth + 1 : 0;
    lastGrowth = growth;
    if (sameGrowth >= 3) return;
    last = h;
    window.parent.postMessage({ type: 'cwu-embed-height', height: h }, target);
  };
  if (typeof ResizeObserver !== 'undefined') new ResizeObserver(send).observe(document.documentElement);
  window.addEventListener('load', send);
  setInterval(send, 1000); // content that loads late (videos, images) without resizing the root
  send();
}

/** /embed, /embed/signin, /embed/preview: shown before any login. */
export const isEmbedPath = (path: string): boolean => path === '/embed' || path.startsWith('/embed/');
